import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Eye, EyeOff, X } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["recomendaciones", "autores", "generos", "novedades", "librerias"];

const EMPTY_FORM = {
    id: null,
    title: "",
    slug: "",
    excerpt: "",
    content_markdown: "",
    category: "recomendaciones",
    tags: "",
    cover_url: "",
    seo_title: "",
    seo_description: "",
    published: false,
};

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | form object

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/blog/posts");
            setPosts(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudieron cargar los posts");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const openNew = () => setEditing({ ...EMPTY_FORM });
    const openEdit = (p) =>
        setEditing({
            ...EMPTY_FORM,
            ...p,
            tags: (p.tags || []).join(", "),
        });

    const save = async () => {
        const payload = {
            title: editing.title,
            slug: editing.slug || undefined,
            excerpt: editing.excerpt,
            content_markdown: editing.content_markdown,
            category: editing.category,
            tags: editing.tags
                ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean)
                : [],
            cover_url: editing.cover_url || null,
            seo_title: editing.seo_title || null,
            seo_description: editing.seo_description || null,
            published: !!editing.published,
        };
        try {
            if (editing.id) {
                await api.patch(`/admin/blog/posts/${editing.id}`, payload);
                toast.success("Post actualizado");
            } else {
                await api.post("/admin/blog/posts", payload);
                toast.success("Post creado");
            }
            setEditing(null);
            await fetchPosts();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al guardar");
        }
    };

    const togglePublished = async (p) => {
        try {
            await api.patch(`/admin/blog/posts/${p.id}`, { published: !p.published });
            toast.success(!p.published ? "Post publicado" : "Post oculto");
            await fetchPosts();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`¿Eliminar «${p.title}»?`)) return;
        try {
            await api.delete(`/admin/blog/posts/${p.id}`);
            toast.success("Post eliminado");
            await fetchPosts();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al eliminar");
        }
    };

    return (
        <main
            data-testid="admin-blog"
            className="min-h-[calc(100vh-73px)] max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-16"
        >
            <div className="flex items-end justify-between border-b border-[var(--border)] pb-8 mb-10">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-2">
                        Admin
                    </p>
                    <h1
                        className="font-display text-4xl md:text-5xl text-[var(--ink)] italic"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96, "slnt" -8' }}
                    >
                        Blog
                    </h1>
                </div>
                <button
                    type="button"
                    data-testid="admin-blog-new"
                    onClick={openNew}
                    className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-5 py-2.5 text-xs uppercase tracking-widest"
                >
                    <Plus size={14} strokeWidth={2} />
                    Nuevo post
                </button>
            </div>

            {loading ? (
                <p className="text-[var(--ink-muted)] italic">Cargando…</p>
            ) : posts.length === 0 ? (
                <p className="text-[var(--ink-muted)] italic border border-dashed border-[var(--border-strong)] p-8 text-center">
                    Aún no hay posts. Crea el primero.
                </p>
            ) : (
                <div className="space-y-2">
                    {posts.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`admin-blog-row-${p.slug}`}
                            className="flex items-center gap-4 border border-[var(--border-strong)] hover:border-[var(--ink)] transition-colors px-5 py-4"
                        >
                            <span
                                className={[
                                    "text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border",
                                    p.published
                                        ? "border-[var(--accent)] text-[var(--accent)]"
                                        : "border-[var(--border-strong)] text-[var(--ink-dim)]",
                                ].join(" ")}
                            >
                                {p.published ? "PUB" : "DRAFT"}
                            </span>
                            {p.auto_generated && (
                                <span
                                    title="Generado automáticamente cada 48h por la IA"
                                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--ink-muted)] text-[var(--ink-muted)]"
                                >
                                    AUTO
                                </span>
                            )}
                            <div className="flex-1 min-w-0">
                                <Link
                                    to={`/blog/${p.slug}`}
                                    className="block font-display text-lg text-[var(--ink)] truncate hover:text-[var(--accent)]"
                                >
                                    {p.title}
                                </Link>
                                <p className="text-xs text-[var(--ink-muted)] truncate">
                                    /{p.slug} · {p.category} · {(p.tags || []).join(", ")}
                                </p>
                            </div>
                            <button
                                type="button"
                                title={p.published ? "Despublicar" : "Publicar"}
                                data-testid={`admin-blog-toggle-${p.slug}`}
                                onClick={() => togglePublished(p)}
                                className="p-2 text-[var(--ink-muted)] hover:text-[var(--accent)]"
                            >
                                {p.published ? <EyeOff size={15} strokeWidth={1.6} /> : <Eye size={15} strokeWidth={1.6} />}
                            </button>
                            <button
                                type="button"
                                title="Editar"
                                data-testid={`admin-blog-edit-${p.slug}`}
                                onClick={() => openEdit(p)}
                                className="p-2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                            >
                                <Edit2 size={15} strokeWidth={1.6} />
                            </button>
                            <button
                                type="button"
                                title="Eliminar"
                                data-testid={`admin-blog-del-${p.slug}`}
                                onClick={() => remove(p)}
                                className="p-2 text-[var(--ink-muted)] hover:text-red-500"
                            >
                                <Trash2 size={15} strokeWidth={1.6} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div
                    data-testid="admin-blog-modal"
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
                    onClick={(e) => e.target === e.currentTarget && setEditing(null)}
                >
                    <div className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] w-full max-w-4xl max-h-[95vh] overflow-y-auto">
                        <div className="sticky top-0 z-10 bg-[var(--bg-elevated)] border-b border-[var(--border)] flex items-center justify-between px-6 py-4">
                            <p className="font-display text-xl text-[var(--ink)] italic">
                                {editing.id ? "Editar post" : "Nuevo post"}
                            </p>
                            <button
                                type="button"
                                data-testid="admin-blog-modal-close"
                                onClick={() => setEditing(null)}
                                className="p-1.5 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                            >
                                <X size={17} strokeWidth={1.6} />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <Field label="Título" testId="admin-blog-f-title">
                                <input
                                    type="text"
                                    value={editing.title}
                                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Slug (opcional, auto del título)" testId="admin-blog-f-slug">
                                <input
                                    type="text"
                                    value={editing.slug}
                                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                                    placeholder="10-libros-como-cien-anos"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Resumen (excerpt, 10-300 car.)" testId="admin-blog-f-excerpt">
                                <textarea
                                    rows={2}
                                    value={editing.excerpt}
                                    onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Contenido (Markdown)" testId="admin-blog-f-md">
                                <textarea
                                    rows={14}
                                    value={editing.content_markdown}
                                    onChange={(e) => setEditing({ ...editing, content_markdown: e.target.value })}
                                    className={`${inputCls} font-mono text-sm leading-relaxed`}
                                    placeholder="## Un título de sección&#10;&#10;Texto en **markdown**..."
                                />
                            </Field>
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label="Categoría">
                                    <select
                                        value={editing.category}
                                        onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                        className={inputCls}
                                    >
                                        {CATEGORIES.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Tags (separadas por coma)">
                                    <input
                                        type="text"
                                        value={editing.tags}
                                        onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                                        placeholder="garcia-marquez, clasicos"
                                        className={inputCls}
                                    />
                                </Field>
                            </div>
                            <Field label="Imagen de portada (URL, opcional)">
                                <input
                                    type="url"
                                    value={editing.cover_url}
                                    onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })}
                                    placeholder="https://…"
                                    className={inputCls}
                                />
                            </Field>
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label="SEO title (max 70)">
                                    <input
                                        type="text"
                                        maxLength={70}
                                        value={editing.seo_title}
                                        onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="SEO description (max 170)">
                                    <input
                                        type="text"
                                        maxLength={170}
                                        value={editing.seo_description}
                                        onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })}
                                        className={inputCls}
                                    />
                                </Field>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
                                <input
                                    type="checkbox"
                                    checked={!!editing.published}
                                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                                    data-testid="admin-blog-f-published"
                                />
                                Publicado (visible en /blog)
                            </label>
                        </div>
                        <div className="sticky bottom-0 bg-[var(--bg-elevated)] border-t border-[var(--border)] flex items-center justify-end gap-3 px-6 py-4">
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 text-xs uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--ink)]"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                data-testid="admin-blog-save"
                                onClick={save}
                                disabled={!editing.title || !editing.excerpt || !editing.content_markdown}
                                className="px-5 py-2.5 text-xs uppercase tracking-widest bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

const inputCls =
    "w-full bg-[var(--bg)] border border-[var(--border-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-[var(--ink)] placeholder-[var(--ink-dim)]";

function Field({ label, testId, children }) {
    return (
        <label className="block" data-testid={testId}>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
